import React from 'react';
import { Flex } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { AccommodationsConsoleHeader } from './components/Header';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { AccommodationMapListSync } from './components/AccommodationMapListSync/AccommodationMapListSync';
import { Skeleton } from '@shared/components/Skeleton';
import { HotelSearchMapLayout } from '@shared/constants/accommodations';

// Full-footprint console skeleton (header + filters + listings), matching the placeholder the
// console renders while loading. Used by GuestHotelSearch to hold the cash-fund gating decision
// without mounting (and fetching for) a console, avoiding a double-mount/flash on the swap.
//
// It has to be told which card and width the console it is holding for will use. This skeleton is
// the FIRST thing on screen and it outlives the console's own placeholder, so defaulting it to the
// shared card is what made the cash-fund hold draw a control tile with a CTA in the price column
// and no fund footer.
//
// NOTE: ListingsPanel.Skeleton → ListingEntry.Skeleton renders a `Container` that consumes the
// AccommodationMapListSync context, so this skeleton must provide it (it is rendered standalone,
// outside the console tree).
/**
 * The map's share of the desktop console, held open while everything loads.
 *
 * Without it both page skeletons stopped at the listings column and left the right half of the
 * window blank white, so the whole layout jumped wider the moment the real map mounted.
 *
 * Only for `FULL`, which is the one layout that shows a map beside the list from the start:
 * `COMPACT` defers the map until the couple opens it, and `MAP_ONLY` renders a different tree
 * altogether — reserving space in either would make the page collapse when loading finishes.
 * Desktop only: on a phone the map is behind a button in every layout.
 */
export const AccommodationsConsoleMapSkeleton = ({ mapLayoutMode }: Readonly<{ mapLayoutMode?: HotelSearchMapLayout }>) => {
  if (mapLayoutMode !== HotelSearchMapLayout.FULL) return null;

  return (
    <Skeleton
      display={{ _: 'none', md: 'block' }}
      flex={1}
      minWidth={0}
      height="100%"
      borderRadius={0}
    />
  );
};

export const AccommodationsConsoleLayoutSkeleton = ({
  showJoyTravelBenefitsFilter = false,
  listingsPanelWidth,
  renderListingEntrySkeleton,
  mapLayoutMode,
}: Readonly<{
  showJoyTravelBenefitsFilter?: boolean;
  /** Match the console this hold leads to, or the column jumps sideways on the swap. */
  listingsPanelWidth?: string;
  /** The card the console this hold leads to will draw; omitted keeps the shared one. */
  renderListingEntrySkeleton?: () => React.ReactNode;
  /** Layout the console this hold leads to will use; decides whether a map pane is reserved. */
  mapLayoutMode?: HotelSearchMapLayout;
}>) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return (
    <AccommodationMapListSync>
      <Flex
        flexDirection="column"
        width="100%"
        height="100dvh"
        maxWidth="100%"
        overflow="auto"
      >
        <AccommodationsConsoleHeader.Skeleton />
        <Flex
          overflow="auto"
          flex={1}
          minHeight={isMobileOrTablet ? '400px' : 0}
        >
          <FiltersPanel.Skeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />
          <ListingsPanel.Skeleton
            isMobileOrTablet={isMobileOrTablet}
            listingsPanelWidth={listingsPanelWidth}
            renderListingEntrySkeleton={renderListingEntrySkeleton}
          />
          <AccommodationsConsoleMapSkeleton mapLayoutMode={mapLayoutMode} />
        </Flex>
      </Flex>
    </AccommodationMapListSync>
  );
};
